import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="performances--performance-field"
export default class extends Controller {
  static values = {
    attendanceIndex: Number
  }

  static outlets = ["choices"]

  static targets = [
    "projectPart",
    "destroyInput",
    "timeSliceSelect",
    "sliceStart",
    "sliceEnd",
    "workManDays",
    "workTimeSpan",
    "workType",
    "tool",
    "toolManDays",
    "toolTimeSpan",
    "toolStartMeter",
    "toolEndMeter",
    "fuelTaxExemption",
    "fuelUsage"
  ]

  connect() {
    this.reindexField()
  }

  // Resolve the attendance rule for this row.
  #attendanceRuleId() {
    if (this.element.dataset.attendanceRuleId) return this.element.dataset.attendanceRuleId
    const field = this.element.closest("tr.attendance-row, .work-edit-area")
      ?.querySelector('[name$="[attendance_rule_id]"]')
    return field ? field.value : ""
  }

  add(event) {
    event.preventDefault()
    const attendance_rule_id = this.#attendanceRuleId()
    const date = this.element.closest("tr.attendance-row").dataset.date
    const url = new URL(`/performances/add_performance`, window.location.origin)
    url.searchParams.append("date", date)
    if (attendance_rule_id) {
      url.searchParams.append("attendance_rule_id", attendance_rule_id)
    }
    get(url, { responseKind: "turbo-stream" })
  }

  remove(event) {
    const wrapper = event.target.closest('.performances-field')

    const parent = wrapper.parentElement

    const visibleRows = Array.from(parent.querySelectorAll('.performances-field'))
      .filter(row => row.style.display !== 'none')

    const isLastVisibleRow = visibleRows.length === 1

    if (this.hasDestroyInputTarget) {
      this.destroyInputTarget.value = true
      wrapper.querySelector(`input[name$="[changed_by]"]`).value = "user"
      const row = wrapper.closest("tr.attendance-row")
      if (row) row.querySelector(`input.attendance[name$="[changed_by]"]`).value = "user"
      wrapper.style.display = "none"
      if (isLastVisibleRow) {
        this.add(event)
      }

      return
    }

    if (isLastVisibleRow) {
      this.#setSelectValue(this.projectPartTarget, "")
      this.#setSelectValue(this.workTypeTarget, "")
      this.#setSelectValue(this.toolTarget, "")
      this.timeSliceSelectTarget.value = ""
      this.sliceStartTarget.value = ""
      this.sliceStartTarget.value = ""
      this.sliceEndTarget.value = ""
      this.workManDaysTarget.value = ""
      this.workTimeSpanTarget.value = ""
      this.toolTimeSpanTarget.value = ""
      this.toolManDaysTarget.value = ""
      return
    }
    wrapper.remove()
    this.reindexField(this.element.closest('td'))
  }

  async projectPartChange(event) {
    const projectPartId = event.currentTarget.value
    if (!projectPartId) return

    const projectPart = await this.#getProjectPartDetails(projectPartId)
    if (!projectPart) return

    const project = projectPart.project
    if (!project) return

    this.#setSelectValue(this.workTypeTarget, project.default_work_type_id)
    this.#setSelectValue(this.toolTarget, project.default_tool_id)
    this.handleToolChange()
  }

  async calculateWorkTimeSpan(event) {
    event.preventDefault()
    const startTime = this.sliceStartTarget.value
    const endTime = this.sliceEndTarget.value
    const attendanceRuleId = this.#attendanceRuleId()
    if (!attendanceRuleId || !startTime || !endTime) return
    const calculation = await this.#getCalculation()
    if (!calculation) return
    this.#updateWorkTimeElements(calculation)
  }

  async timeSliceChange(event) {
    event.preventDefault()
    const attendance_rule_id = this.#attendanceRuleId()
    const time_slice = this.element.querySelector(".time-slice-name").value
    if (!attendance_rule_id) {
      alert("Select an attendance rule first.")
      return
    }

    const time_slice_id_field = this.element.querySelector(`input[name$="[time_slice_id]"], select[name$="[time_slice_id]"]`)

    if (!time_slice) {
      if (time_slice_id_field) time_slice_id_field.value = ""
      if (this.sliceStartTarget && this.sliceEndTarget) {
        this.sliceStartTarget.value = ""
        this.sliceEndTarget.value = ""
      }
      this.workTimeSpanTarget.value = ""
      this.workManDaysTarget.value = ""
      if (this.toolTarget.value) {
        this.handleToolChange()
      }
      return
    }

    const fetched = await this.#getTimeSliceByName(attendance_rule_id, time_slice)
    if (!fetched) {
      console.warn(`[performance-field] no time slice found for attendance_rule_id=${attendance_rule_id}, name=${time_slice} (online=${navigator.onLine})`)
      return
    }

    const startTime = fetched.start_time
    const endTime = fetched.end_time
    if (this.sliceStartTarget && this.sliceEndTarget) {
      this.sliceStartTarget.value = startTime || ""
      this.sliceEndTarget.value = endTime || ""
    }
    if (time_slice_id_field) time_slice_id_field.value = fetched.id;

    const man_days = fetched.man_days;
    const duration = fetched.duration || 0;
    const hours = Math.floor(duration / 60);
    const minutes = Math.round((duration / 60 - hours) * 60);
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    this.workTimeSpanTarget.value = timeString;
    this.workManDaysTarget.value = man_days != null ? Number(man_days).toFixed(1) : ""
    if (this.toolTarget.value) {
      this.handleToolChange()
    }
  }

  // UPDATE 機械時間 & 機械工数
  handleToolChange() {
    if (!this.toolTarget.value || (!this.workManDaysTarget.value && !this.workTimeSpanTarget.value)) {
      this.toolManDaysTarget.value = null;
      this.toolTimeSpanTarget.value = null;
      return;
    };
    this.calculateMeterSpan();
  }

  calculateMeterSpan() {
    if (!this.hasToolStartMeterTarget || !this.hasToolEndMeterTarget) return
    const startVal = parseFloat(this.toolStartMeterTarget.value)
    const endVal = parseFloat(this.toolEndMeterTarget.value)
    if (isNaN(startVal) || isNaN(endVal)) return

    const diff = endVal - startVal
    if (diff < 0) return

    let hours = Math.floor(diff)
    let minutes = Math.round((diff - hours) * 60)
    if (minutes >= 60) {
      hours += 1
      minutes = 0
    }
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`

    if (this.hasToolTimeSpanTarget) {
      this.toolTimeSpanTarget.value = timeString
    }

    const ruleId = this.#attendanceRuleId()
    let prescribedHours = 8.0
    if (ruleId) {
      const masterData = this.#getMasterData()
      const rule = masterData?.attendance_rules?.[String(ruleId)]
      if (rule && rule.daily_work_time > 0) {
        prescribedHours = rule.daily_work_time / 60.0
      }
    }
    const manDays = (diff / prescribedHours).toFixed(1)
    if (this.hasToolManDaysTarget) {
      this.toolManDaysTarget.value = manDays
    }
  }

  reindexField(container = this.element.closest('td')) {
    if (!container) return
    const allFields = Array.from(container.querySelectorAll('.performances-field'))

    allFields.forEach((field, index) => {
      field.dataset.index = index.toString()

      field.querySelectorAll('[name]').forEach(input => {
        if (input.name) {
          input.name = input.name.replace(
            /performances_attributes\]\[[^\]]+\]/g,
            `performances_attributes][${index}]`
          )
        }
      })

      field.querySelectorAll('[id]').forEach(input => {
        if (input.id) {
          input.id = input.id.replace(
            /performances_attributes_.*?_([a-z_]+)$/,
            `performances_attributes_${index}_$1`
          )
        }
      })
    })
  }

  async #getProjectPartDetails(projectPartId) {
    if (!navigator.onLine) {
      return this.#getProjectPartFromMasterData(projectPartId)
    }

    const url = new URL(`/project_parts/${projectPartId}`, window.location.origin)

    try {
      const response = await get(url, { responseKind: "json" })
      if (!response.ok) throw new Error("Failed to fetch project part details")
      return await response.json
    } catch (error) {
      console.error("Error fetching project part details:", error)
      return this.#getProjectPartFromMasterData(projectPartId)
    }
  }

  async #getTimeSliceByName(attendanceRuleId, name) {
    if (!navigator.onLine) {
      return this.#getTimeSliceFromMasterData(attendanceRuleId, name)
    }

    const url = new URL(`/attendance_rules/${attendanceRuleId}/time_slices/by_name/${name}`, window.location.origin)

    try {
      const response = await get(url, { responseKind: "json" })
      if (!response.ok) throw new Error("Failed to fetch time slice")
      return await response.json
    } catch (error) {
      console.error("Error fetching time slice:", error)
      return this.#getTimeSliceFromMasterData(attendanceRuleId, name)
    }
  }

  #getMasterData() {
    const el = document.getElementById("performance-master-data")
    if (!el) {
      console.warn("[performance-field] #performance-master-data element not found in DOM")
      return null
    }
    try {
      return JSON.parse(el.textContent)
    } catch (e) {
      console.error("[performance-field] failed to parse performance master data", e)
      return null
    }
  }

  #getProjectPartFromMasterData(projectPartId) {
    const masterData = this.#getMasterData()
    const cached = masterData?.project_parts?.[String(projectPartId)]
    if (!cached) return null
    return {
      project: {
        default_work_type_id: cached.default_work_type_id,
        default_tool_id: cached.default_tool_id
      }
    }
  }

  #getTimeSliceFromMasterData(attendanceRuleId, name) {
    const masterData = this.#getMasterData()
    return masterData?.time_slices?.[`${attendanceRuleId}:${name}`] || null
  }

  markChangedByUser(event) {
    const field = event.currentTarget;
    const container = field.closest(".performances-field");
    container.querySelector(`input[name$="[changed_by]"]`).value = "user";
  }

  #setSelectValue(selectElement, value) {
    if (!selectElement) return

    if (selectElement.closest(`[data-controller="choices"]`)) {
      const outlet = this.choicesOutlets.find(outlet => outlet.element.contains(selectElement))
      if (outlet) {
        outlet.setValue(value)
      }
    } else {
      selectElement.value = value;
    }
  }

  async #getCalculation() {
    const clockIn = this.sliceStartTarget.value
    const clockOut = this.sliceEndTarget.value
    const date = this.element.closest("tr.attendance-row, .work-edit-area").dataset.date
    const attendanceRuleId = this.#attendanceRuleId()

    if (!navigator.onLine) {
      return this.#calculateOffline(clockIn, clockOut, attendanceRuleId)
    }

    const url = new URL(`/performances/calculate_work_time`, window.location.origin)
    const searchParams = new URLSearchParams({
      clock_in: clockIn,
      clock_out: clockOut,
      date: date,
      attendance_rule_id: attendanceRuleId
    })
    url.search = searchParams.toString()

    try {
      const res = await get(url, { responseKind: "json" })
      if (!res.ok) throw res
      return await res.json
    } catch (error) {
      let message = "Failed to calculate work time."
      try {
        const data = await error.json
        message = data.error || message
      } catch (_) {}
      console.error(message)
      return this.#calculateOffline(clockIn, clockOut, attendanceRuleId)
    }
  }

  #calculateOffline(clockIn, clockOut, attendanceRuleId) {
    if (!clockIn || !clockOut) return null
    const masterData = this.#getMasterData()
    const rule = masterData?.attendance_rules?.[String(attendanceRuleId)]
    if (!rule) return null

    const totalMinutes = this.#diffMinutes(clockIn, clockOut)
    if (totalMinutes <= 0) return null

    const breakMinutes = (rule.break_times || []).reduce((sum, br) => {
      return sum + this.#overlapMinutes(clockIn, clockOut, br.start, br.end)
    }, 0)
    const workMinutes = Math.max(0, totalMinutes - breakMinutes)
    const manDays = rule.daily_work_time > 0 ? workMinutes / rule.daily_work_time : 0

    return { total: workMinutes, man_days: manDays }
  }

  #toMinutes(time) {
    const [h, m] = time.split(":").map(Number)
    return h * 60 + m
  }

  #diffMinutes(start, end) {
    return this.#toMinutes(end) - this.#toMinutes(start)
  }

  #overlapMinutes(s1, e1, s2, e2) {
    const start = Math.max(this.#toMinutes(s1), this.#toMinutes(s2))
    const end = Math.min(this.#toMinutes(e1), this.#toMinutes(e2))
    return Math.max(0, end - start)
  }

  #updateWorkTimeElements(result) {
    if (result.total) {
      const hours = Math.floor(result.total / 60)
      const minutes = result.total % 60
      this.workTimeSpanTarget.value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      this.workTimeSpanTarget.value = timeString

      this.workManDaysTarget.value = result.man_days != null ? Number(result.man_days).toFixed(1) : "";
      if (this.toolTarget.value) {
        this.handleToolChange()
      }
    }
  }
}
